import { useEffect } from 'react';
import { User, Phone, Check, Plus, Trash2, Star, AlertCircle } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Separator } from '~/components/ui/separator';
import { AddressForm } from '~/components/forms/AddressForm';
import { ContactListForm } from '~/components/forms/ContactListForm';
import { ChurchAffiliationForm } from '~/components/forms/ChurchAffiliationForm';
import { useTabReviewTracker } from '~/hooks/useTabReviewTracker';

const ALL_TABS = ['address', 'guardians', 'emergency', 'pickup', 'church'];

interface FamilyInfoChunkProps {
  familyInfo: any;
  onUpdate: (familyInfo: any) => void;
  onReviewComplete?: (reviewed: boolean) => void;
  validationErrors?: { field: string; message: string }[];
}

/**
 * Reusable family info chunk with tabs for address, guardians, emergency contacts, pickup, church.
 * Used by both reenrollment FamilyInfoStep and application AppFamilyInfoStep.
 */
export function FamilyInfoChunk({ familyInfo, onUpdate, onReviewComplete, validationErrors = [] }: FamilyInfoChunkProps) {
  const address = familyInfo?.address || {};
  const guardians = familyInfo?.guardians || [];
  const emergencyContacts = familyInfo?.emergencyContacts || [];
  const authorizedPickup = familyInfo?.authorizedPickup || [];
  const churchAffiliation = familyInfo?.churchAffiliation || {};

  const { activeId, setActiveId, visitedIds, allVisited } = useTabReviewTracker(ALL_TABS, onReviewComplete);

  // Auto-navigate to the tab with the first error
  const hasGuardianErrors = validationErrors.some(e => e.field.startsWith('guardians.'));
  useEffect(() => {
    if (hasGuardianErrors) {
      setActiveId('guardians');
    }
  }, [hasGuardianErrors, setActiveId]);

  // Helper: check if a specific field has a validation error
  const hasFieldError = (field: string) => validationErrors.some(e => e.field === field);

  const updateField = (field: string, value: any) => {
    onUpdate({
      ...familyInfo,
      [field]: value,
    });
  };

  const updateAddress = (field: string, value: string) => {
    updateField('address', {
      ...address,
      [field]: value,
    });
  };

  const updateGuardian = (index: number, field: string, value: any) => {
    const updated = [...guardians];
    updated[index] = { ...updated[index], [field]: value };
    updateField('guardians', updated);
  };

  const addGuardian = () => {
    const isFirst = guardians.length === 0;
    const newGuardian = {
      tempId: crypto.randomUUID(),
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      relationship: 'GUARDIAN',
      isPrimary: isFirst,
    };
    updateField('guardians', [...guardians, newGuardian]);
  };

  const removeGuardian = (index: number) => {
    const removed = guardians[index];
    const updated = guardians.filter((_: any, i: number) => i !== index);
    // If we removed the primary, make the first remaining one primary
    if (removed.isPrimary && updated.length > 0) {
      updated[0] = { ...updated[0], isPrimary: true };
    }
    updateField('guardians', updated);
  };

  const setPrimaryGuardian = (index: number) => {
    const updated = guardians.map((g: any, i: number) => ({
      ...g,
      isPrimary: i === index,
    }));
    updateField('guardians', updated);
  };

  const updateChurch = (field: string, value: string) => {
    updateField('churchAffiliation', {
      ...churchAffiliation,
      [field]: value,
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      {!allVisited && validationErrors.length === 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Please review each tab before continuing. Click on each tab to review the information.
        </div>
      )}
      <Tabs value={activeId} onValueChange={setActiveId}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="address" className="relative">
            Address
            {visitedIds.has('address') && (
              <Check className="h-3 w-3 ml-1 text-green-600" />
            )}
          </TabsTrigger>
          <TabsTrigger value="guardians" className="relative">
            Parents
            {hasGuardianErrors ? (
              <AlertCircle className="h-3 w-3 ml-1 text-red-500" />
            ) : visitedIds.has('guardians') && (
              <Check className="h-3 w-3 ml-1 text-green-600" />
            )}
          </TabsTrigger>
          <TabsTrigger value="emergency" className="relative">
            Emergency
            {visitedIds.has('emergency') && (
              <Check className="h-3 w-3 ml-1 text-green-600" />
            )}
          </TabsTrigger>
          <TabsTrigger value="pickup" className="relative">
            Pickup
            {visitedIds.has('pickup') && (
              <Check className="h-3 w-3 ml-1 text-green-600" />
            )}
          </TabsTrigger>
          <TabsTrigger value="church" className="relative">
            Church
            {visitedIds.has('church') && (
              <Check className="h-3 w-3 ml-1 text-green-600" />
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="address" className="mt-6">
          <AddressForm address={address} onChange={updateAddress} />
        </TabsContent>

        <TabsContent value="guardians" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Parents / Guardians
              </CardTitle>
              <CardDescription>
                Contact information for parents or guardians. At least one is required and must be designated as the primary contact.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {guardians.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No parents or guardians added yet. Please add at least one.
                </p>
              ) : (
                guardians.map((guardian: any, index: number) => (
                  <div key={guardian.personId || guardian.tempId || index}>
                    {index > 0 && <Separator className="mb-6" />}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Select
                            value={guardian.relationship || 'GUARDIAN'}
                            onValueChange={(value) => updateGuardian(index, 'relationship', value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FATHER">Father</SelectItem>
                              <SelectItem value="MOTHER">Mother</SelectItem>
                              <SelectItem value="GUARDIAN">Guardian</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          {guardian.isPrimary ? (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center gap-1">
                              <Star className="h-3 w-3 fill-current" />
                              Primary Contact
                            </span>
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground hover:text-primary"
                              onClick={() => setPrimaryGuardian(index)}
                            >
                              <Star className="h-3 w-3 mr-1" />
                              Set as Primary
                            </Button>
                          )}
                          {guardians.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => removeGuardian(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`guardian-first-${index}`}>First Name</Label>
                          <Input
                            id={`guardian-first-${index}`}
                            value={guardian.firstName || ''}
                            onChange={(e) => updateGuardian(index, 'firstName', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`guardian-last-${index}`}>Last Name</Label>
                          <Input
                            id={`guardian-last-${index}`}
                            value={guardian.lastName || ''}
                            onChange={(e) => updateGuardian(index, 'lastName', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`guardian-email-${index}`} className={guardian.isPrimary && hasFieldError('guardians.primaryEmail') ? 'text-red-600' : ''}>
                            Email {guardian.isPrimary && <span className="text-xs text-muted-foreground">(required for primary)</span>}
                          </Label>
                          <Input
                            id={`guardian-email-${index}`}
                            type="email"
                            value={guardian.email || ''}
                            onChange={(e) => updateGuardian(index, 'email', e.target.value)}
                            className={guardian.isPrimary && hasFieldError('guardians.primaryEmail') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`guardian-phone-${index}`}>Phone</Label>
                          <Input
                            id={`guardian-phone-${index}`}
                            type="tel"
                            value={guardian.phone || ''}
                            onChange={(e) => updateGuardian(index, 'phone', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={addGuardian}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Parent / Guardian
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emergency" className="mt-6">
          <ContactListForm
            contacts={emergencyContacts}
            onChange={(contacts) => updateField('emergencyContacts', contacts)}
            icon={Phone}
            title="Emergency Contacts"
            description="People we can contact in case of emergency (other than parents)"
            addButtonLabel="Add Emergency Contact"
            emptyMessage="No emergency contacts added yet."
          />
        </TabsContent>

        <TabsContent value="pickup" className="mt-6">
          <ContactListForm
            contacts={authorizedPickup}
            onChange={(contacts) => updateField('authorizedPickup', contacts)}
            icon={User}
            title="Authorized Pickup"
            description="People authorized to pick up your children (other than parents)"
            addButtonLabel="Add Authorized Pickup Person"
            emptyMessage="No authorized pickup persons added yet."
          />
        </TabsContent>

        <TabsContent value="church" className="mt-6">
          <ChurchAffiliationForm
            churchAffiliation={churchAffiliation}
            onChange={updateChurch}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
